/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import axios from '../apis/axiosConfig';
import Layout from '../common/Layout';
import ConfirmModal from '../common/ConfirmModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaTrash, FaFileExcel, FaFileCsv, FaChevronDown, FaSync } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1';

const mapCategoryName = (raw) => {
  if (!raw) return '';
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();
  
  if (lower === 'wardrobe' || lower === 'sliding' || lower === 'sliding wardrobe' || lower === 'sliding wardrobes') {
    return 'Sliding Wardrobes';
  }
  if (lower === 'openable' || lower === 'openable wardrobe' || lower === 'openable wardrobes') {
    return 'Openable Wardrobes';
  }
  if (lower === 'kitchen' || lower === 'modular kitchen' || lower === 'modular kitchens') {
    return 'Modular Kitchens';
  }
  if (lower === 'interiors' || lower === 'partition' || lower === 'room partition' || lower === 'room partitions') {
    return 'Room Partition';
  }
  return trimmed;
};

// Helper to extract clean city and category
const parseConsultationData = (item) => {
  const msg = item.message || '';
  let city = item.city || '';
  let rawCategory = item.category || '';

  // Extract City from message if not in item.city
  if (!city || city === 'Website') {
    const cityMatch = msg.match(/(?:from|City:)\s*([^,]+)/i);
    if (cityMatch && cityMatch[1] && cityMatch[1].trim() !== 'N/A' && cityMatch[1].trim() !== 'Website') {
      city = cityMatch[1].trim();
    } else {
      city = item.city || '-';
    }
  }

  // If rawCategory is empty or generic, check if message has Category: ...
  if (!rawCategory || rawCategory.toLowerCase().includes('consultation') || rawCategory.toLowerCase().includes('general') || rawCategory.toLowerCase().includes('contact')) {
    const catMatch = msg.match(/Category:\s*([^,\n\r]+(?:,\s*[^,\n\r]+)*)/i);
    if (catMatch && catMatch[1]) {
      rawCategory = catMatch[1].trim();
    }
  }

  // Format category list
  let finalCategories = [];
  if (rawCategory) {
    const parts = rawCategory.split(',').map((p) => mapCategoryName(p)).filter(Boolean);
    finalCategories = Array.from(new Set(parts));
  }

  // Fallback if still empty or generic
  if (finalCategories.length === 0 || (finalCategories.length === 1 && (finalCategories[0].toLowerCase().includes('consultation') || finalCategories[0].toLowerCase().includes('general')))) {
    if (msg.toLowerCase().includes('kitchen')) finalCategories = ['Modular Kitchens'];
    else if (msg.toLowerCase().includes('sliding')) finalCategories = ['Sliding Wardrobes'];
    else if (msg.toLowerCase().includes('openable')) finalCategories = ['Openable Wardrobes'];
    else if (msg.toLowerCase().includes('partition') || msg.toLowerCase().includes('interiors')) finalCategories = ['Room Partition'];
    else if (msg.toLowerCase().includes('wardrobe')) finalCategories = ['Sliding Wardrobes'];
    else finalCategories = ['Free Consultation'];
  }

  return {
    ...item,
    parsedCity: city || '-',
    parsedCategory: finalCategories.join(', '),
    categoriesList: finalCategories,
  };
};

const formatDate = (dateStr) => {
  if (!dateStr) return { dateOnly: 'N/A', fullFormatted: 'N/A' };
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { dateOnly: dateStr, fullFormatted: dateStr };

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');

    const dateOnly = `${day}/${month}/${year}`;
    const fullFormatted = `${dateOnly} ${strHours}:${minutes} ${ampm}`;
    return { dateOnly, fullFormatted };
  } catch (e) {
    return { dateOnly: dateStr, fullFormatted: dateStr };
  }
};

const getLocalDateString = (d) => {
  if (!d) return '';
  const dateObj = typeof d === 'string' ? new Date(d.replace(' ', 'T')) : d;
  if (isNaN(dateObj.getTime())) return '';
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ConsultantEnquiries = () => {
  const [rawEnquiries, setRawEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'today' | 'yesterday' | 'last3days' | 'all' | 'custom'
  const [customDate, setCustomDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const pageSize = 10;

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/enquiries?type=consultation`);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRawEnquiries(data);
    } catch (err) {
      console.error('Failed to fetch consultant enquiries:', err);
      toast.error('Failed to load consultation enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const parsedEnquiries = useMemo(() => {
    return rawEnquiries.map(parseConsultationData);
  }, [rawEnquiries]);

  // Date Filtering & Counter Calculations
  const now = new Date();
  const todayStr = getLocalDateString(now);

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const yesterdayStr = getLocalDateString(yesterday);

  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(now.getDate() - 3);
  threeDaysAgo.setHours(0, 0, 0, 0);

  const stats = useMemo(() => {
    let todayCount = 0;
    let yesterdayCount = 0;
    let last3DaysCount = 0;

    parsedEnquiries.forEach((item) => {
      const rawDate = item.date_updated || item.scheduled_date || item.createdAt;
      if (!rawDate) return;
      const d = new Date(String(rawDate).replace(' ', 'T'));
      if (isNaN(d.getTime())) return;

      const itemDateStr = getLocalDateString(d);
      if (itemDateStr === todayStr) todayCount++;
      if (itemDateStr === yesterdayStr) yesterdayCount++;
      if (d >= threeDaysAgo) last3DaysCount++;
    });

    return {
      today: todayCount,
      yesterday: yesterdayCount,
      last3Days: last3DaysCount,
      all: parsedEnquiries.length,
    };
  }, [parsedEnquiries, todayStr, yesterdayStr, threeDaysAgo]);

  // Filtered List
  const filteredList = useMemo(() => {
    return parsedEnquiries.filter((item) => {
      // 1. Date Filter
      const rawDate = item.date_updated || item.scheduled_date || item.createdAt;
      const d = rawDate ? new Date(String(rawDate).replace(' ', 'T')) : null;
      const itemDateStr = d ? getLocalDateString(d) : '';

      if (dateFilter === 'today') {
        if (!d || itemDateStr !== todayStr) return false;
      } else if (dateFilter === 'yesterday') {
        if (!d || itemDateStr !== yesterdayStr) return false;
      } else if (dateFilter === 'last3days') {
        if (!d || d < threeDaysAgo) return false;
      } else if (dateFilter === 'custom' && customDate) {
        if (!d || itemDateStr !== customDate) return false;
      }

      // 2. Search Filter
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      return (
        (item.name && item.name.toLowerCase().includes(term)) ||
        (item.parsedCity && item.parsedCity.toLowerCase().includes(term)) ||
        (item.parsedCategory && item.parsedCategory.toLowerCase().includes(term)) ||
        (item.mobile && item.mobile.includes(term)) ||
        (item.email && item.email.toLowerCase().includes(term)) ||
        (item.message && item.message.toLowerCase().includes(term))
      );
    });
  }, [parsedEnquiries, dateFilter, customDate, searchTerm, todayStr, yesterdayStr, threeDaysAgo]);

  // Pagination calculations
  const totalEntries = filteredList.length;
  const totalPages = Math.ceil(totalEntries / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const currentEntries = filteredList.slice(startIndex, startIndex + pageSize);

  // Checkbox handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(currentEntries.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleOpenDeleteModal = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete?.id) return;
    try {
      setDeleteLoading(true);
      await axios.delete(`${API_BASE}/enquiries/${itemToDelete.id}`);
      toast.success('Enquiry deleted successfully');
      setRawEnquiries((prev) => prev.filter((item) => item.id !== itemToDelete.id));
      setSelectedIds((prev) => prev.filter((item) => item !== itemToDelete.id));
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExportCSV = (exportAll = false) => {
    const listToExport = exportAll ? parsedEnquiries : (filteredList.length > 0 ? filteredList : parsedEnquiries);
    if (listToExport.length === 0) {
      toast.info('No records available to export');
      return;
    }
    const headers = ['#', 'Client Name', 'Mobile Number', 'Email Address', 'City', 'Category / Requirement', 'Enquiry Date', 'Message'];
    const rows = listToExport.map((item, idx) => [
      idx + 1,
      `"${(item.name || '').replace(/"/g, '""')}"`,
      `"${item.mobile || ''}"`,
      `"${item.email || ''}"`,
      `"${item.parsedCity || ''}"`,
      `"${item.parsedCategory || ''}"`,
      `"${formatDate(item.date_updated).dateOnly}"`,
      `"${(item.message || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Aristo_Consultation_Enquiries_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowDownloadDropdown(false);
    toast.success(`Excel / CSV sheet downloaded successfully (${listToExport.length} leads)!`);
  };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Top Header Bar */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center" style={{ gap: '14px' }}>
            <h1 className="h3 font-weight-bold mb-0 text-white" style={{ letterSpacing: '0.5px' }}>
              Enquiry Lists
            </h1>
            <button
              type="button"
              onClick={fetchEnquiries}
              style={{
                background: 'transparent',
                border: '1px solid rgba(196, 168, 130, 0.3)',
                color: '#c4a882',
                borderRadius: '6px',
                padding: '4px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Refresh Data"
            >
              <FaSync className={loading ? 'fa-spin' : ''} size={11} /> Refresh
            </button>
          </div>

          {/* Unified Download Dropdown Button */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              style={{
                backgroundColor: '#1b2a4a',
                color: '#ffffff',
                border: '1px solid rgba(196, 168, 130, 0.35)',
                padding: '9px 18px',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              }}
            >
              <span>Download</span>
              <FaChevronDown size={11} style={{ transition: 'transform 0.2s', transform: showDownloadDropdown ? 'rotate(180deg)' : 'none' }} />
            </button>

            {showDownloadDropdown && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 6px)',
                  backgroundColor: '#141310',
                  border: '1px solid rgba(196, 168, 130, 0.35)',
                  borderRadius: '10px',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.8)',
                  zIndex: 9999,
                  minWidth: '180px',
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    handleExportCSV(false);
                    setShowDownloadDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(196,168,130,0.18)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <FaFileExcel color="#c4a882" size={14} /> Download Excel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleExportCSV(false);
                    setShowDownloadDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(196,168,130,0.18)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <FaFileCsv color="#c4a882" size={14} /> Download CSV
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 4 Date-based Filter Cards (Responsive Grid) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          {/* Card 1: Today */}
          <button
            type="button"
            onClick={() => { setDateFilter('today'); setCurrentPage(1); }}
            style={{
              width: '100%',
              textAlign: 'left',
              backgroundColor: '#141310',
              border: dateFilter === 'today' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderLeft: '4px solid #48bb78',
              borderRadius: '12px',
              padding: '16px 20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: dateFilter === 'today' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block',
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {String(stats.today).padStart(2, '0')}{' '}
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>Today</div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{formatDate(todayStr).dateOnly}</div>
          </button>

          {/* Card 2: Yesterday */}
          <button
            type="button"
            onClick={() => { setDateFilter('yesterday'); setCurrentPage(1); }}
            style={{
              width: '100%',
              textAlign: 'left',
              backgroundColor: '#141310',
              border: dateFilter === 'yesterday' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '12px',
              padding: '16px 20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: dateFilter === 'yesterday' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block',
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {String(stats.yesterday).padStart(2, '0')}{' '}
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>Yesterday</div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{formatDate(yesterdayStr).dateOnly}</div>
          </button>

          {/* Card 3: Last 3 Days */}
          <button
            type="button"
            onClick={() => { setDateFilter('last3days'); setCurrentPage(1); }}
            style={{
              width: '100%',
              textAlign: 'left',
              backgroundColor: '#141310',
              border: dateFilter === 'last3days' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '12px',
              padding: '16px 20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: dateFilter === 'last3days' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block',
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {String(stats.last3Days).padStart(2, '0')}{' '}
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>Last 3 Days</div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              {formatDate(threeDaysAgo).dateOnly} to {formatDate(todayStr).dateOnly}
            </div>
          </button>

          {/* Card 4: All Time / Custom Date */}
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (!e.target.closest('input') && !e.target.closest('button')) {
                setDateFilter('all');
                setCustomDate('');
                setCurrentPage(1);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (!e.target.closest('input') && !e.target.closest('button')) {
                  setDateFilter('all');
                  setCustomDate('');
                  setCurrentPage(1);
                }
              }
            }}
            style={{
              width: '100%',
              textAlign: 'left',
              backgroundColor: '#141310',
              border: dateFilter === 'all' || dateFilter === 'custom' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '12px',
              padding: '16px 20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: (dateFilter === 'all' && !customDate) || dateFilter === 'custom' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block',
              position: 'relative'
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {stats.all}{' '}
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>
              {dateFilter === 'custom' && customDate ? 'Filtered Date' : 'Custom Date'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
              <input
                type="date"
                value={customDate}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomDate(val);
                  if (val) {
                    setDateFilter('custom');
                  } else {
                    setDateFilter('all');
                  }
                  setCurrentPage(1);
                }}
                style={{
                  backgroundColor: '#080807',
                  border: dateFilter === 'custom' && customDate ? '1px solid #c4a882' : '1px solid rgba(196,168,130,0.4)',
                  color: '#c4a882',
                  fontSize: '11px',
                  borderRadius: '6px',
                  padding: '3px 6px',
                  outline: 'none',
                  cursor: 'pointer',
                  colorScheme: 'dark'
                }}
              />
              {customDate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomDate('');
                    setDateFilter('all');
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    color: '#c4a882',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    lineHeight: 1
                  }}
                  title="Clear Filter"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Table Card Container */}
        <div
          style={{
            backgroundColor: '#12110e',
            border: '1px solid rgba(196, 168, 130, 0.25)',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Top Controls: Filter status + Search Bar */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: '10px' }}>
            <div style={{ color: '#8a8377', fontSize: '13px' }}>
              Showing {dateFilter === 'today' ? 'Today' : dateFilter === 'yesterday' ? 'Yesterday' : dateFilter === 'last3days' ? 'Last 3 Days' : 'All'} Enquiries
              {dateFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => { setDateFilter('all'); setCustomDate(''); }}
                  style={{
                    marginLeft: '10px',
                    background: 'none',
                    border: 'none',
                    color: '#c4a882',
                    fontSize: '12px',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Reset Filter
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#ccc', fontWeight: 500 }}>Search:</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder=""
                style={{
                  backgroundColor: '#080807',
                  border: '1px solid rgba(196, 168, 130, 0.3)',
                  borderRadius: '6px',
                  color: '#fff',
                  padding: '6px 12px',
                  fontSize: '13px',
                  outline: 'none',
                  width: '180px',
                }}
              />
            </div>
          </div>

          {/* Table Responsive Wrapper */}
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                color: '#fff',
                fontSize: '13px',
              }}
            >
              <thead>
                <tr style={{ color: '#c4a882', fontSize: '12px', fontWeight: 700, borderBottom: '1px solid rgba(196,168,130,0.25)', textAlign: 'left' }}>
                  <th style={{ width: '40px', padding: '12px 8px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={currentEntries.length > 0 && selectedIds.length === currentEntries.length}
                      style={{ cursor: 'pointer', accentColor: '#c4a882' }}
                    />
                  </th>
                  <th style={{ width: '35px', padding: '12px 8px' }}>#</th>
                  <th style={{ minWidth: '220px', padding: '12px 8px' }}>Enquiry from</th>
                  <th style={{ minWidth: '130px', padding: '12px 8px' }}>City</th>
                  <th style={{ minWidth: '160px', padding: '12px 8px' }}>Category</th>
                  <th style={{ minWidth: '130px', padding: '12px 8px' }}>Enquiry Date</th>
                  <th style={{ width: '70px', padding: '12px 8px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5" style={{ color: '#8a8377' }}>
                      <div className="spinner-border text-warning" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div className="mt-2">Loading consultation enquiries...</div>
                    </td>
                  </tr>
                ) : currentEntries.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5" style={{ color: '#8a8377' }}>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#c4a882', marginBottom: '4px' }}>
                        No Enquiries Found
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        All previous records cleaned up. New website leads will appear here automatically!
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentEntries.map((item, index) => {
                    const rowNumber = startIndex + index + 1;
                    const dateFormatted = formatDate(item.date_updated || item.scheduled_date);
                    const isSelected = selectedIds.includes(item.id);

                    return (
                      <tr
                        key={item.id || index}
                        style={{
                          backgroundColor: isSelected ? 'rgba(196, 168, 130, 0.12)' : index % 2 === 0 ? '#161512' : '#141310',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                        }}
                      >
                        {/* Checkbox */}
                        <td style={{ padding: '14px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectRow(item.id)}
                            style={{ cursor: 'pointer', accentColor: '#c4a882' }}
                          />
                        </td>

                        {/* # */}
                        <td style={{ padding: '14px 8px', verticalAlign: 'middle', color: '#888', fontWeight: 600 }}>
                          {rowNumber}
                        </td>

                        {/* Enquiry from: Name + Phone + Email */}
                        <td style={{ padding: '14px 8px', verticalAlign: 'middle' }}>
                          <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '14px' }}>
                            {item.name || 'Anonymous'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#ddd', marginTop: '2px' }}>
                            {item.mobile || '-'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#8a8377', marginTop: '2px' }}>
                            {item.email || '-'}
                          </div>
                        </td>

                        {/* City */}
                        <td style={{ padding: '14px 8px', verticalAlign: 'middle', color: '#fff', fontWeight: 500 }}>
                          {item.parsedCity}
                        </td>

                        {/* Category Badges */}
                        <td style={{ padding: '14px 8px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {item.categoriesList && item.categoriesList.length > 0 ? (
                              item.categoriesList.map((cat, ci) => (
                                <span
                                  key={ci}
                                  style={{
                                    display: 'inline-block',
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(196, 168, 130, 0.35)',
                                    backgroundColor: 'rgba(196, 168, 130, 0.08)',
                                    color: '#c4a882',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {cat}
                                </span>
                              ))
                            ) : (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '5px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(196, 168, 130, 0.35)',
                                  backgroundColor: 'rgba(196, 168, 130, 0.08)',
                                  color: '#c4a882',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                {item.parsedCategory}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Enquiry Date */}
                        <td style={{ padding: '14px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap', color: '#ccc' }}>
                          {dateFormatted.dateOnly}
                        </td>

                        {/* Action: Trash Delete Icon */}
                        <td style={{ padding: '14px 8px', verticalAlign: 'middle', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenDeleteModal(item)}
                            style={{
                              background: 'none',
                              border: '1px solid rgba(220, 53, 69, 0.25)',
                              padding: '6px 9px',
                              borderRadius: '6px',
                              color: '#ff6b6b',
                              cursor: 'pointer',
                              backgroundColor: 'rgba(220, 53, 69, 0.08)',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.25)';
                              e.currentTarget.style.color = '#fff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.08)';
                              e.currentTarget.style.color = '#ff6b6b';
                            }}
                            title="Delete"
                          >
                            <FaTrash size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer: Showing entries & Pagination */}
          <div className="d-flex flex-wrap justify-content-between align-items-center pt-3" style={{ borderTop: '1px solid rgba(196,168,130,0.15)', fontSize: '13px', color: '#8a8377' }}>
            <div>
              Showing {totalEntries === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + pageSize, totalEntries)} of {totalEntries} entries
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: currentPage === 1 ? '#555' : '#fff',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px',
                  fontSize: '13px',
                }}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                const isActive = pg === currentPage;
                return (
                  <button
                    type="button"
                    key={pg}
                    onClick={() => setCurrentPage(pg)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: isActive ? '#c4a882' : '#1e1c18',
                      color: isActive ? '#080807' : '#fff',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {pg}
                  </button>
                );
              })}

              <button
                type="button"
                disabled={currentPage === totalPages || totalEntries === 0}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: currentPage === totalPages || totalEntries === 0 ? '#555' : '#fff',
                  cursor: currentPage === totalPages || totalEntries === 0 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px',
                  fontSize: '13px',
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Animated Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        type="delete"
        title="Delete Consultation Enquiry"
        itemName={itemToDelete?.name || 'this enquiry'}
        itemType="enquiry"
        message={
          itemToDelete ? (
            <>
              Are you sure you want to delete the consultation enquiry from{' '}
              <span className="confirm-item-highlight">"{itemToDelete.name || 'Anonymous'}"</span>?
              <div className="confirm-warning-note">This record will be permanently removed and cannot be recovered.</div>
            </>
          ) : null
        }
        confirmText="Yes, Delete"
        cancelText="Cancel"
        loading={deleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleteLoading) {
            setDeleteModalOpen(false);
            setItemToDelete(null);
          }
        }}
      />
    </Layout>
  );
};

export default ConsultantEnquiries;
